import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import notify from 'components/atoms/Notify'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'

const GenerateWebhooksModal = ({ onSuccess, close, partnerId, webhookData, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    webhookUrl: '',
    description: '',
    isEnabled: true,
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isEdit && webhookData) {
      setFormData({
        name: webhookData.name || '',
        webhookUrl: webhookData.webhookUrl || '',
        description: webhookData.description || '',
        isEnabled: webhookData.isEnabled !== undefined ? webhookData.isEnabled : true,
      })
    }
  }, [isEdit, webhookData])

  const webhookSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    webhookUrl: Yup.string().url('Must be a valid URL').required('Webhook URL is required'),
    description: Yup.string(),
    isEnabled: Yup.boolean(),
  })

  const handleChange = e => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleGenerateWebhook = async () => {
    try {
      setLoading(true)
      await webhookSchema.validate(formData, { abortEarly: false })

      let response
      if (isEdit && webhookData) {
        response = await apiPut(`webhook/${webhookData._id}`, formData)
        if (response.success) {
          notify.success('Webhook updated successfully')
          onSuccess(response.data)
        } else {
          notify.error(response.message || 'Failed to update webhook')
        }
      } else {
        response = await apiPost(`partner/${partnerId}/webhook`, formData)
        if (response.success) {
          notify.success('Webhook created successfully')
          onSuccess(response.data)
        } else {
          notify.error(response.message || 'Failed to create webhook')
        }
      }
    } catch (validationError) {
      if (validationError.inner) {
        const newErrors = {}
        // eslint-disable-next-line
        validationError.inner.forEach(err => {
          newErrors[err.path] = err.message
        })
        setErrors(newErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="mb-4">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-dark-500 focus:border-dark-500"
          placeholder="Enter webhook name"
        />
        {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
      </div>

      <div className="mb-4">
        <label htmlFor="webhookUrl" className="block text-sm font-medium text-gray-700 mb-1">
          Webhook URL *
        </label>
        <input
          type="url"
          name="webhookUrl"
          id="webhookUrl"
          value={formData.webhookUrl}
          onChange={handleChange}
          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-dark-500 focus:border-dark-500"
          placeholder="https://example.com/webhook"
        />
        {errors.webhookUrl && <p className="text-sm text-red-600 mt-1">{errors.webhookUrl}</p>}
      </div>

      <div className="mb-4">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          name="description"
          id="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-dark-500 focus:border-dark-500"
          placeholder="Enter webhook description"
        />
        {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
      </div>

      <div className="mb-4">
        <div className="flex items-center">
          <input
            id="isEnabled"
            name="isEnabled"
            type="checkbox"
            checked={formData.isEnabled}
            onChange={handleChange}
            className="h-4 w-4 text-dark-600 focus:ring-dark-500 border-gray-300 rounded"
          />
          <label htmlFor="isEnabled" className="ml-2 text-sm text-gray-700">
            Enabled
          </label>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          disabled={loading}
          onClick={handleGenerateWebhook}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {loading ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update Webhook' : 'Create Webhook'}
        </button>
        <button
          type="button"
          className="ml-2 inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200"
          onClick={close}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export default GenerateWebhooksModal
